import { useState, useCallback } from 'react';
import type { InitiativeEntry, Token } from '../types';
import type { UseChatReturn } from './useChat';
import { useI18n } from '../i18n';

export interface UseInitiativeReturn {
  entries: InitiativeEntry[];
  currentIndex: number;
  round: number;
  rollAll: (tokens: Token[]) => InitiativeEntry[];
  next: () => void;
  reset: () => void;
  setEntries: React.Dispatch<React.SetStateAction<InitiativeEntry[]>>;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  setRound: React.Dispatch<React.SetStateAction<number>>;
}

export function useInitiative(chat: UseChatReturn): UseInitiativeReturn {
  const { t } = useI18n();
  const [entries, setEntries] = useState<InitiativeEntry[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [round, setRound] = useState(0);

  const rollAll = useCallback((tokens: Token[]): InitiativeEntry[] => {
    const newEntries: InitiativeEntry[] = tokens.map(tok => ({
      tokenId: tok.id,
      name: tok.name,
      value: Math.floor(Math.random() * 20) + 1,
      color: tok.color,
    })).sort((a, b) => b.value - a.value);

    setEntries(newEntries);
    setCurrentIndex(0);
    setRound(1);

    const summary = newEntries.map(e => `${e.name}: ${e.value}`).join(', ');
    chat.addSystemMessage(`${t('initiative.roundStart')} ${summary}`);

    return newEntries;
  }, [chat, t]);

  const next = useCallback(() => {
    setEntries(prev => {
      if (prev.length === 0) return prev;
      setCurrentIndex(ci => {
        const nextIdx = (ci + 1) % prev.length;
        if (nextIdx === 0) {
          setRound(r => {
            const newRound = r + 1;
            chat.addSystemMessage(`${t('initiative.round')} ${newRound}`);
            return newRound;
          });
        }
        const current = prev[nextIdx];
        chat.addSystemMessage(`${t('initiative.turn')} ${current.name}`);
        return nextIdx;
      });
      return prev;
    });
  }, [chat, t]);

  const reset = useCallback(() => {
    setEntries([]);
    setCurrentIndex(-1);
    setRound(0);
  }, []);

  return { entries, currentIndex, round, rollAll, next, reset, setEntries, setCurrentIndex, setRound };
}
